import { Component, DestroyRef, EventEmitter, inject, Input, OnInit, Output } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { DEFAULT_TIMEZONE, WIDGET_DISPLAY_TIMEZONE_SOURCE } from '@constants/app.constants';
import { TimezoneDbModel } from '@models/timezone.model';
import { NgSelectModule } from '@ng-select/ng-select';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { CalioTranslationPipe } from '@pipes/calio-translation.pipe';
import { BookingService } from '@services/feature/booking.service';
import { TimezoneService } from '@services/feature/timezone.service';
import { finalize } from 'rxjs';

const modules = [NgSelectModule, FormsModule, TranslateModule];
const pipes = [CalioTranslationPipe];

@Component({
  standalone: true,
  selector: 'app-timezone-select',
  imports: [...modules, ...pipes],
  templateUrl: './timezone-select.component.html',
  styleUrl: './timezone-select.component.scss'
})
export class TimezoneSelectComponent implements OnInit {

  private timezoneService = inject(TimezoneService);
  private bookingService = inject(BookingService);
  private destroyRef = inject(DestroyRef);
  private translateService = inject(TranslateService);

  @Input() protected timezones = new Array<TimezoneDbModel>();

  @Output('onTimezoneChange') onTimezoneChangeEvent = new EventEmitter<string>();

  protected isTimezonesLoading = false;
  protected selectedTimezone: string;
  protected lang: string;

  ngOnInit(): void {
    this.lang = this.translateService.getDefaultLang();
    this.translateService.onLangChange.subscribe(language => this.lang = language.lang);
    this.timezones?.length === 0 && this.getTimezones();
    this.preselectLocalTimezone();
  }

  private getTimezones() {
    this.isTimezonesLoading = true;
    this.timezoneService.getTimezones()
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.isTimezonesLoading = false)
      )
      .subscribe(timezones => this.timezones = timezones?.data || []);
  }

  protected get usePartnerTimezone(): boolean {
    return this.bookingService.partnerData?.widget_display_timezone_source === WIDGET_DISPLAY_TIMEZONE_SOURCE.PARTNER
      && !!this.bookingService.partnerData?.timezone;
  }

  protected get partnerTimezoneLabel(): string {
    if (!this.usePartnerTimezone) return '';
    const tz = this.bookingService.partnerData!.timezone!;
    const found = this.timezones.find(t => t.zone === tz);
    return found?.name ?? tz;
  }

  private preselectLocalTimezone(): void {
    // When partner has opted into 'partner' mode, force partner.timezone as the display TZ.
    // Otherwise preserve today's behaviour: existing pick → visitor browser TZ.
    if (this.usePartnerTimezone) {
      this.selectedTimezone = this.bookingService.partnerData!.timezone!;
      this.bookingService.selectedBookingData.timezone = this.selectedTimezone;
      return;
    }
    const preferred = this.bookingService.selectedBookingData?.timezone || this.timezoneService.getLocalTimezone();
    const foundLocalTimezone = this.timezones.find(timezone => timezone.zone_group.includes(preferred));
    this.selectedTimezone = foundLocalTimezone ? foundLocalTimezone.zone : DEFAULT_TIMEZONE;
    this.bookingService.selectedBookingData.timezone = this.selectedTimezone;
  }

  protected onTimezoneChange(timezone: string) {
    this.onTimezoneChangeEvent.emit(timezone);
  }
}
